/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useHistory } from "react-router-dom";
import { Row as RowType } from "react-table";
import { Box, Heading, Loader } from "@nordcloud/gnui";
import { QueryPagination, Table } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { FilteredSearch } from "~/components/Search/FilteredSearch";
import { Tool } from "~/models";
import { ACTIONS, Can, ROUTES } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import { ToolAdd } from "./ToolAdd";
import { ToolListColumns } from "./ToolList";

function ToolPage() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [toolList, setToolList] = React.useState({
    list: [],
    totalCount: 0,
  });
  const [filteredList, setFilteredList] = React.useState([]);
  const [{ limit, page }] = useQueryState();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchToolsList = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/tools?limit=${limit}&page=${page}`);
      setToolList({
        list: data?.tools,
        totalCount: data?.totalCount,
      });
      setFilteredList(data?.tools);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, limit, page, showError]);

  React.useEffect(() => {
    fetchToolsList();
  }, [fetchToolsList]);

  const columns = React.useMemo(() => ToolListColumns, []);

  const history = useHistory();

  const handleRowKeyPress = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    selected: Tool
  ) => {
    if (e.key === "Enter") {
      onRedirectHandler(selected);
    }
  };

  const onRedirectHandler = (tool: Tool) => {
    history.push(`${ROUTES.tool.index}/${tool.id}`);
  };

  return (
    <Container>
      <Row>
        <Col>
          <Box
            css={{ display: "flex", alignItems: "center" }}
            spacing="spacing04"
          >
            <Heading level={2}>Tooling</Heading>
            <Can action={ACTIONS.ADD_TOOL}>
              <ToolAdd />
            </Can>
          </Box>
          <Box spacing="spacing04">
            <Box boxStyle="lightGrey" spacing="spacing04">
              <FilteredSearch
                mode="tools"
                endpoint="tools"
                redirectLink={ROUTES.tool.index}
                options={{ keys: ["id", "name"] }}
              />
            </Box>
            {isLoading ? (
              <Loader inContent />
            ) : (
              <>
                <Table
                  getRowProps={(row: RowType) => ({
                    onClick: () => onRedirectHandler(row.original as Tool),
                    onKeyPress: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                      handleRowKeyPress(e, row.original as Tool),
                    className: "interactive",
                    tabIndex: 0,
                    role: "button",
                  })}
                  columns={columns}
                  data={filteredList || []}
                />
                <QueryPagination count={toolList.totalCount} />
              </>
            )}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export { ToolPage };
